import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { kanbanStatsService } from '@/services/kanbanStatsService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress } from '@/components/ui/Progress';
import { MetricLabel } from '@/components/Dashboard/MetricLabel';
import { MetricDefinitionButton } from '@/components/Dashboard/MetricDefinitionButton';
import {
  BarChart3,
  Users,
  FileText,
  DollarSign,
  AlertCircle,
} from 'lucide-react';

interface KanbanStatsTabProps {
  filters?: {
    companyId?: string;
    operadorId?: string;
    period?: string;
  };
}

export const KanbanStatsTab: React.FC<KanbanStatsTabProps> = ({ filters }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['kanban-stats', filters],
    queryFn: () => kanbanStatsService.getKanbanStats(filters),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Erro ao carregar estatísticas
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Não foi possível carregar as estatísticas dos kanbans.
        </p>
      </div>
    );
  }

  if (!data?.data) {
    return (
      <div className="text-center py-8">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Nenhum dado encontrado
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Não há estatísticas disponíveis para os filtros selecionados.
        </p>
      </div>
    );
  }

  const { kanbans, resumoGeral, meta } = data.data;
  const definicoesResumo = meta?.definicoes ?? [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const getKanbanDefs = (kanbanNome: string) => meta?.definicoesPorKanban?.[kanbanNome] ?? definicoesResumo;

  return (
    <div className="space-y-6">
      {meta?.notaVisaoGeral ? (
        <p className="text-xs text-muted-foreground bg-muted/50 border border-border rounded-md px-3 py-2">
          {meta.notaVisaoGeral}
          {meta.inicioHoje ? (
            <span className="block mt-1">
              Referência de &quot;vencido&quot;: vencimento anterior a{' '}
              {new Date(meta.inicioHoje).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (início do dia civil).
            </span>
          ) : null}
        </p>
      ) : null}

      {/* Resumo Geral */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <MetricLabel
                    label="Total de Kanbans"
                    metricId="resumo_totalKanbans"
                    definicoes={definicoesResumo}
                  />
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {resumoGeral.totalKanbans}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <MetricLabel
                    label="Total de Títulos"
                    metricId="resumo_totalTitulos"
                    definicoes={definicoesResumo}
                  />
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(resumoGeral.totalTitulos)}
                </p>
              </div>
              <FileText className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <MetricLabel
                    label="Valor Total"
                    metricId="resumo_totalValor"
                    definicoes={definicoesResumo}
                  />
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(resumoGeral.totalValor)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <MetricLabel
                    label="Operadores Ativos"
                    metricId="resumo_totalOperadores"
                    definicoes={definicoesResumo}
                  />
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {resumoGeral.totalOperadores}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Estatísticas por Kanban */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {kanbans.map((kanban) => {
          const kanbanDefs = getKanbanDefs(kanban.nome);
          const est = kanban.estatisticas;
          const showCancelados = est.titulosCancelados > 0;
          const showOutros = est.titulosSemStatusExibido > 0;

          const statusRows = [
            { key: 'titulosAbertos', label: 'Abertos', metricId: 'titulosAbertos' },
            { key: 'titulosVencidos', label: 'Vencidos', metricId: 'titulosVencidos' },
            { key: 'titulosParciais', label: 'Parciais', metricId: 'titulosParciais' },
            { key: 'titulosFechados', label: 'Fechados', metricId: 'titulosFechados' },
            ...(showCancelados
              ? [{ key: 'titulosCancelados', label: 'Cancelados', metricId: 'titulosCancelados' }]
              : []),
            ...(showOutros
              ? [{ key: 'titulosSemStatusExibido', label: 'Outros / não classificados', metricId: 'titulosSemStatusExibido' }]
              : []),
          ];

          const clienteRows = [
            { key: 'clientesAbertos', label: 'Abertos', tituloKey: 'titulosAbertos', metricId: 'clientesAbertos' },
            { key: 'clientesVencidos', label: 'Vencidos', tituloKey: 'titulosVencidos', metricId: 'clientesVencidos' },
            { key: 'clientesParciais', label: 'Parciais', tituloKey: 'titulosParciais', metricId: 'clientesParciais' },
            { key: 'clientesFechados', label: 'Fechados', tituloKey: 'titulosFechados', metricId: 'clientesFechados' },
            ...(showCancelados
              ? [{ key: 'clientesCancelados', label: 'Cancelados', tituloKey: 'titulosCancelados', metricId: 'titulosCancelados' }]
              : []),
          ];

          const valorRows = [
            { key: 'valorAberto', label: 'Abertos', metricId: 'titulosAbertos' },
            { key: 'valorVencido', label: 'Vencidos', metricId: 'titulosVencidos' },
            { key: 'valorParcial', label: 'Parciais', metricId: 'titulosParciais' },
            { key: 'valorFechado', label: 'Fechados', metricId: 'titulosFechados' },
            ...(showCancelados
              ? [{ key: 'valorCancelado', label: 'Cancelados', metricId: 'titulosCancelados' }]
              : []),
            ...(showOutros
              ? [{ key: 'valorSemStatusExibido', label: 'Outros / não classificados', metricId: 'titulosSemStatusExibido' }]
              : []),
          ];

          return (
            <Card key={kanban.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: kanban.cor }}
                    />
                    {kanban.nome}
                  </CardTitle>
                  {kanban.isDefault && <Badge variant="secondary">Padrão</Badge>}
                </div>
                {kanban.descricao && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">{kanban.descricao}</p>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatNumber(est.totalTitulos)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <MetricLabel
                        label="Total de Títulos"
                        metricId="totalTitulos"
                        definicoes={kanbanDefs}
                      />
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(est.valorTotal)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <MetricLabel
                        label="Valor Total"
                        metricId="valorTotal"
                        definicoes={kanbanDefs}
                      />
                    </p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 inline-flex items-center gap-1">
                      <MetricLabel
                        label="Taxa de Recuperação"
                        metricId="taxaRecuperacao"
                        definicoes={kanbanDefs}
                      />
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {est.taxaRecuperacao.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={est.taxaRecuperacao} className="h-2" />
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Status dos Títulos
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {statusRows.map(({ key, label, metricId }) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                      >
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          <MetricLabel label={label} metricId={metricId} definicoes={kanbanDefs} />
                        </span>
                        <Badge variant="outline">
                          {formatNumber(est[key as keyof typeof est] as number)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Clientes Distintos por Status
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {clienteRows.map(({ key, label, tituloKey, metricId }) => {
                      const clientes = (est[key as keyof typeof est] as number) || 0;
                      const titulos = (est[tituloKey as keyof typeof est] as number) || 0;
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              <MetricLabel label={label} metricId={metricId} definicoes={kanbanDefs} />
                            </span>
                            <span className="text-xs text-gray-500">{formatNumber(titulos)} títulos</span>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {formatNumber(clientes)} clientes
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between p-2 bg-blue-100 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        <MetricLabel
                          label="Total de Clientes"
                          metricId="totalClientes"
                          definicoes={kanbanDefs}
                        />
                      </span>
                    </div>
                    <Badge className="bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-100">
                      {formatNumber(est.totalClientes || 0)}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Valores por Status
                  </h4>
                  <div className="space-y-1">
                    {valorRows.map(({ key, label, metricId }) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          <MetricLabel label={label} metricId={metricId} definicoes={kanbanDefs} />
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(est[key as keyof typeof est] as number)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      <MetricLabel
                        label="Operadores Ativos"
                        metricId="operadoresAtivos"
                        definicoes={kanbanDefs}
                      />
                    </span>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {est.operadoresAtivos}
                  </Badge>
                </div>

                {kanban.etapas.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white inline-flex items-center gap-1">
                      Etapas ({kanban.etapas.length})
                      <MetricDefinitionButton
                        metricId="etapa_titulosCount"
                        definicoes={kanbanDefs}
                      />
                    </h4>
                    <div className="space-y-1">
                      {kanban.etapas.map((etapa) => (
                        <div
                          key={etapa.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: etapa.cor }}
                            />
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {etapa.nome}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatNumber(etapa.clientesCount || 0)} clientes
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{formatNumber(etapa.titulosCount)}</Badge>
                            <span className="text-xs text-gray-500">
                              {formatCurrency(etapa.valorTotal)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
